import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Send, Compass } from "lucide-react";
import { streamChat } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { track } from "@/lib/analytics";
import { CONCIERGE_QUICK_PROMPTS, GUIDE } from "@/data/portfolio";
import GuideIcon from "@/components/site/GuideIcon";
import GuidedTour from "@/components/site/GuidedTour";
import { useLingerNudge, useSectionIntro, NudgeBubble } from "@/components/site/Nudges";

function makeSessionId() {
  return `web-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

const GREETING = { role: "assistant", content: GUIDE.greeting };
const WELCOME_KEY = "sinthusiast_welcomed";

export default function AIConcierge() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [welcome, setWelcome] = useState(false);
  const [tourStep, setTourStep] = useState(null);
  const [messages, setMessages] = useState([GREETING]);
  useEffect(() => {
    setMessages((m) => (m.length === 1 ? [{ role: "assistant", content: t(GUIDE.greeting) }] : m));
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(makeSessionId());
  const scrollRef = useRef(null);
  const { pathname } = useLocation();
  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/tools") || pathname.endsWith("/print") || pathname === "/portfolio-pdf" || pathname === "/cover-letter";
  const [nudge, closeNudge, showNudge] = useLingerNudge(!hidden && !open && !welcome && tourStep === null);
  useSectionIntro("journey", !hidden && !open && tourStep === null, (id) => {
    dismissWelcome();
    showNudge(id);
  });

  useEffect(() => {
    if (sessionStorage.getItem(WELCOME_KEY) || hidden) return;
    const t = setTimeout(() => {
      if (!sessionStorage.getItem(WELCOME_KEY)) setWelcome(true);
    }, 1400);
    return () => clearTimeout(t);
  }, [hidden]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const dismissWelcome = () => {
    sessionStorage.setItem(WELCOME_KEY, "1");
    setWelcome(false);
  };

  const startTour = () => {
    dismissWelcome();
    setOpen(false);
    setTourStep(0);
    track("tour_start");
  };

  const exitTour = (finished) => {
    if (finished === true) track("tour_complete");
    setTourStep(null);
  };

  const askAbout = (step) => {
    setTourStep(null);
    setOpen(true);
    send(`Tell me more about "${step.title}", what should I look at?`);
  };

  const send = async (text) => {
    const content = text ?? input;
    if (!content.trim() || streaming) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [
      ...m,
      { role: "user", content },
      { role: "assistant", content: "" },
    ]);
    setInput("");
    setStreaming(true);
    track("concierge_message", { text: content.slice(0, 120), quick: text != null });

    await streamChat({
      sessionId: sessionRef.current,
      message: content,
      history,
      lang,
      onDelta: (delta) => {
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + delta };
          return copy;
        });
      },
      onDone: () => setStreaming(false),
      onError: () => {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Ugh, I lost my voice for a second. Tap the retry button and I'll try again, or head to Contact to reach Sinthu directly.",
            retry: content,
          };
          return copy;
        });
        setStreaming(false);
      },
    }).catch(() => setStreaming(false));
  };

  if (hidden) return null;

  return (
    <>
      {tourStep !== null && (
        <GuidedTour step={tourStep} onStep={setTourStep} onExit={exitTour} onAsk={askAbout} />
      )}

      {/* Floating trigger */}
      {tourStep === null && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
          {!open && !welcome && (
            <span className="hidden sm:inline-flex px-3 py-1.5 rounded-full bg-white border border-[#e5e0d8] shadow-md font-mono-label text-[9px] text-[#14352a]">
              {GUIDE.name}
            </span>
          )}
          <button
            data-testid="ai-concierge-toggle"
            onClick={() => {
              if (welcome) dismissWelcome();
              setOpen((v) => !v);
            }}
            className={`relative h-14 w-14 rounded-full grid place-items-center btn-guide ${
              open ? "" : "ring-pulse wiggle"
            }`}
            aria-label={`Open ${GUIDE.name}`}
          >
            {open ? <X className="h-5 w-5" /> : <GuideIcon className="h-8 w-8" />}
          </button>
        </div>
      )}

      {/* Linger nudge */}
      {nudge && !open && !welcome && tourStep === null && (
        <NudgeBubble nudge={nudge} onClose={closeNudge} />
      )}

      {/* Welcome bubble */}
      {welcome && !open && tourStep === null && (
        <div
          data-testid="guide-welcome"
          className="fixed bottom-24 right-6 z-50 w-[min(92vw,340px)] p-5 bg-white border border-[#e5e0d8] shadow-2xl rounded-2xl fade-up"
        >
          <button
            data-testid="guide-welcome-dismiss"
            onClick={dismissWelcome}
            className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-full hover:bg-[#f2ece4]"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-full btn-guide grid place-items-center">
              <GuideIcon className="h-5 w-5" />
            </span>
            <div>
              <div className="font-display text-base leading-tight">{GUIDE.name}</div>
              <div className="font-mono-label text-[8px] text-[#86928a]">{t(GUIDE.tagline)}</div>
            </div>
          </div>
          <p className="mt-3 text-sm text-[#14352a] leading-relaxed">{t(GUIDE.welcome)}</p>
          <div className="mt-4 flex gap-2">
            <button
              data-testid="guide-tour-btn"
              onClick={startTour}
              className="flex-1 h-10 rounded-full btn-guide text-sm font-medium inline-flex items-center justify-center gap-1.5"
            >
              <Compass className="h-4 w-4" /> {t("Take the tour")}
            </button>
            <button
              data-testid="guide-chat-btn"
              onClick={() => {
                dismissWelcome();
                setOpen(true);
              }}
              className="flex-1 h-10 rounded-full border border-[#14352a] text-sm font-medium hover:bg-[#14352a] hover:text-white transition-colors"
            >
              {t("Ask me anything")}
            </button>
          </div>
        </div>
      )}

      {/* Chat panel */}
      {open && tourStep === null && (
        <div
          data-testid="ai-concierge-panel"
          className="fixed bottom-24 right-6 z-50 w-[min(92vw,380px)] max-h-[70vh] flex flex-col bg-white border border-[#e5e0d8] shadow-2xl rounded-2xl overflow-hidden fade-up"
        >
          <div className="p-4 bg-[#14352a] text-[#faf8f5] flex items-center gap-3">
            <span className="w-9 h-9 rounded-full btn-guide grid place-items-center">
              <GuideIcon className="h-6 w-6" />
            </span>
            <div className="flex-1">
              <div className="font-display text-base leading-tight">{GUIDE.name}</div>
              <div className="font-mono-label text-[8px] text-[#faf8f5]/60">
                {t(GUIDE.tagline)} · Claude · live
              </div>
            </div>
            <button
              data-testid="concierge-start-tour-btn"
              onClick={startTour}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-white/20 text-[11px] hover:bg-white/10"
            >
              <Compass className="h-3.5 w-3.5" /> Tour
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#faf8f5]">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`concierge-msg-${m.role}-${i}`}
                className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed rounded-2xl ${
                  m.role === "user"
                    ? "ml-auto bg-[#14352a] text-[#faf8f5] rounded-br-sm"
                    : "bg-white border border-[#e5e0d8] text-[#14352a] rounded-bl-sm"
                }`}
              >
                {m.retry && (
                  <button
                    data-testid="concierge-retry-btn"
                    onClick={() => {
                      setMessages((prev) => prev.slice(0, -2));
                      setTimeout(() => send(m.retry), 0);
                    }}
                    className="block mb-2 font-mono-label text-[9px] text-[#9a7218] link-underline"
                  >
                    ↻ Retry
                  </button>
                )}
                {m.content || (
                  <span className="inline-flex gap-1">
                    <span className="w-1 h-1 bg-[#86928a] rounded-full animate-bounce" />
                    <span className="w-1 h-1 bg-[#86928a] rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                    <span className="w-1 h-1 bg-[#86928a] rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
                  </span>
                )}
              </div>
            ))}
          </div>

          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 bg-[#faf8f5]">
              {CONCIERGE_QUICK_PROMPTS.map((p) => (
                <button
                  key={p}
                  data-testid={`concierge-prompt-${p.slice(0, 10).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => send(p)}
                  className="text-[11px] px-3 py-1.5 border border-[#e5e0d8] text-[#55625b] bg-white rounded-full hover:border-[#d9a92e] hover:text-[#9a7218] transition-colors"
                >
                  {t(p)}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-3 border-t border-[#e5e0d8] bg-white flex gap-2"
          >
            <input
              data-testid="concierge-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("Ask about a case study, method, or her experience…")}
              className="flex-1 h-10 px-3 text-sm bg-[#faf8f5] border border-[#e5e0d8] rounded-full focus:outline-none focus:border-[#d9a92e]"
            />
            <button
              data-testid="concierge-send-btn"
              type="submit"
              disabled={streaming || !input.trim()}
              className="h-10 w-10 grid place-items-center btn-guide rounded-full disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
